import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { FileSignature, Loader2, CheckCircle2, FileDown } from "lucide-react";

function lastMonths(n) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export default function VoiOverrides() {
  const { state } = useAuth();
  const me = state?.user;
  const canSign = ["owner", "manager"].includes(me?.role);
  const [params, setParams] = useSearchParams();
  const months = lastMonths(13);
  const month = params.get("month") || months[1];
  const [rep, setRep] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setRep(null);
    axios.get(`${API_BASE}/voi/override-reports/${month}`).then((r) => setRep(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const signed = rep?.signoffs?.some((s) => s.user_id === me?.user_id);
  const sign = async () => {
    setBusy(true);
    try { setRep((await axios.post(`${API_BASE}/voi/override-reports/${month}/signoff`, { note })).data); setNote(""); toast.success(`Signed off ${month}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="voi-overrides-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[var(--ink)]">ID override sign-off</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1 max-w-xl">Every job completed without a verified client ID, by month, for partner review.</p>
        </div>
        <select value={month} onChange={(e) => setParams({ month: e.target.value })} data-testid="voi-overrides-month"
                className="border border-[var(--border)] rounded-md px-3 py-2 text-[13px] bg-white font-mono">
          {months.map((m, i) => <option key={m} value={m}>{m}{i === 0 ? " (this month, preview)" : ""}</option>)}
        </select>
      </div>
      {!rep ? <Loader2 className="animate-spin text-[var(--ink-3)]" /> : (
        <>
          {rep.items.length === 0 ? (
            <div className="text-[13px] text-[var(--ink-3)] py-10 text-center border border-dashed border-[var(--border)] rounded-md mb-6" data-testid="voi-overrides-empty">
              No ID-check overrides in {month}.
            </div>
          ) : (
            <div className="card overflow-x-auto mb-6">
              <table className="w-full text-[12px]" data-testid="voi-overrides-table">
                <thead className="text-left text-[var(--ink-3)] uppercase tracking-wider text-[10px]">
                  <tr>{["When", "Job", "Client", "Overridden by", "Reason"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {rep.items.map((i, k) => (
                    <tr key={`${i.job_id}-${k}`} data-testid={`voi-override-row-${k}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{new Date(i.at).toLocaleString()}</td>
                      <td className="px-3 py-2"><a href={`/jobs?job_id=${i.job_id}`} className="hover:underline">{i.job_title}</a></td>
                      <td className="px-3 py-2">{i.client_name || i.client_id}</td>
                      <td className="px-3 py-2">{i.by_name}</td>
                      <td className="px-3 py-2 max-w-[320px]">{i.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card p-5 space-y-3" data-testid="voi-overrides-signoff">
            <div className="overline">Sign-off</div>
            {rep.preview ? (
              <p className="text-[13px] text-[var(--ink-2)]" data-testid="voi-overrides-preview-note">This month is still running — sign-off opens on the 1st, when the report is emailed to owners and managers.</p>
            ) : (
              <>
                {(rep.signoffs || []).length === 0 && <p className="text-[13px] text-[var(--ink-3)]">Not signed off yet.</p>}
                {(rep.signoffs || []).map((s) => (
                  <div key={s.user_id} className="flex items-start gap-2 text-[13px]" data-testid={`voi-signoff-${s.user_id}`}>
                    <CheckCircle2 size={15} className="text-[var(--moss)] mt-0.5" />
                    <div><b>{s.name}</b> ({s.role}) signed off {new Date(s.at).toLocaleString()}{s.note ? <div className="text-[var(--ink-2)]">“{s.note}”</div> : null}</div>
                  </div>
                ))}
                {canSign && !signed && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Optional note"
                           className="flex-1 min-w-[240px] border border-[var(--border)] rounded-md px-3 py-2 text-[13px]" data-testid="voi-signoff-note" />
                    <button className="btn-primary" onClick={sign} disabled={busy} data-testid="voi-signoff-btn">
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />} Sign off {month}
                    </button>
                  </div>
                )}
                {(rep.signoffs || []).length > 0 && (
                  <button className="btn-secondary" data-testid="voi-overrides-pdf" onClick={async () => {
                    try {
                      const r = await axios.get(`${API_BASE}/voi/override-reports/${month}/pdf`, { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a"); a.href = url; a.download = `id-overrides-${month}-signed.pdf`; a.click();
                      setTimeout(() => URL.revokeObjectURL(url), 2000);
                      toast.success("Signed PDF downloaded");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}><FileDown size={13} /> Download signed PDF</button>
                )}
                {!canSign && <p className="text-[12px] text-[var(--ink-3)]">Only owners and managers can sign off.</p>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
